import { FC, RefObject, useCallback, useRef } from 'react'
import { isUndefined } from 'lodash'

import {
  chain,
  ColorMode,
  Flex,
  Text,
  ThemeProvider,
  Token,
  Tooltip,
  useTheme,
  VStack,
} from '@revolut-internal/ui-kit'

import { ADDITION_SUFFIX, ADDITIONAL_DATA_KEY, MAIN_DATA_KEY } from 'consts/chart'
import { ChartData, ChartDataValue } from 'types'
import { calculatePercentageDifference } from 'utils/calculatePercentageDifference'
import { formatBlankLabel } from 'utils/formatBlankLabel'
import { formatNumber } from 'utils/formatNumber'
import { getFormattedDateTime } from 'utils/formatTimeAndDuration'
import { pluralize } from 'utils/pluralize'

const OUTER_CIRCLE_SIZE = 24

const INNER_CIRCLE_SIZE = 8
const INNER_CIRCLE_STROKE = 2.5
const INNER_CIRCLE_RADIUS = (INNER_CIRCLE_SIZE / 2 + INNER_CIRCLE_STROKE / 2) / 2 // strokeWidth decreases the radius by its half

type LineChartDotProps = {
  dataKeyProperty: string
  entityName: string
  cx?: number
  cy?: number
  stroke?: string
  payload?: ChartData[0]
  primaryLabel?: string
  additionLabel?: string
  value?: ChartDataValue
  strokeOpacity?: number | string
  isShown?: boolean
  isTooltipOpen?: boolean
  isAddition?: boolean
}
type TooltipContentProps = {
  value: ChartDataValue
  time: number
  dataKeyProperty: string
  entityName: string
  extraValue?: ChartDataValue
  primaryLabel?: string
  additionLabel?: string
  isAddition: boolean
}

const TooltipContent: FC<TooltipContentProps> = ({
  value,
  time,
  dataKeyProperty,
  primaryLabel,
  additionLabel,
  extraValue,
  entityName,
  isAddition,
}) => {
  const extraValuePercentage =
    value.hasData &&
    !isUndefined(extraValue) &&
    (isAddition
      ? extraValue && calculatePercentageDifference(value.value, extraValue.value)
      : calculatePercentageDifference(extraValue.value, value.value))
  const isPrimaryLabelSymbol = primaryLabel?.length && primaryLabel?.length === 1

  return (
    <VStack space="s-4" p="s-4">
      <Flex alignItems="center" justifyContent="space-between">
        {isAddition && <Text variant="emphasis2">{additionLabel}</Text>}
        <Text variant="emphasis2">
          {value.hasData ? formatNumber(value.value) : 'No data'}
          {value.hasData &&
            primaryLabel &&
            (isPrimaryLabelSymbol
              ? primaryLabel
              : ` ${pluralize(primaryLabel, value.value, true)}`)}
        </Text>
      </Flex>
      {!isUndefined(extraValue) && (
        <Flex alignItems="center" justifyContent="space-between">
          <Text variant="body3">{isAddition ? 'Actual' : additionLabel}:</Text>
          <Text variant="body3">
            {chain(
              extraValue.hasData ? formatNumber(extraValue.value) : 'No data',
              extraValue.hasData && extraValuePercentage && (
                <Text color="warning">{formatNumber(extraValuePercentage)}%</Text>
              ),
            )}
          </Text>
        </Flex>
      )}
      <Flex alignItems="center" justifyContent="space-between">
        <Text variant="body3">Time:</Text>
        <Text variant="body3">{getFormattedDateTime(time)}</Text>
      </Flex>
      <Flex alignItems="start" justifyContent="space-between">
        <Text variant="body3" whiteSpace="nowrap" flex="0 0 auto">
          {entityName}:
        </Text>
        <Text ml="s-8" variant="body3" textAlign="right">
          {formatBlankLabel(dataKeyProperty)}
        </Text>
      </Flex>
    </VStack>
  )
}

export const LineChartDot: FC<LineChartDotProps> = ({
  cx,
  cy,
  stroke,
  payload,
  dataKeyProperty,
  primaryLabel,
  additionLabel,
  strokeOpacity,
  entityName,
  isShown,
  isTooltipOpen = false,
  isAddition = false,
}) => {
  const anchorRef = useRef<SVGSVGElement>(null)
  const mainDataKeyProperty = dataKeyProperty.replace(ADDITION_SUFFIX, '')

  const { mode } = useTheme()

  const disableTooltipContainerPointerEvents = useCallback(
    (tooltip: HTMLDivElement | null) => {
      const container = tooltip?.parentElement

      if (!container) {
        return undefined
      }

      container.style.pointerEvents = 'none'

      return () => {
        container.style.pointerEvents = 'unset'
      }
    },
    [],
  )

  if (
    typeof cx !== 'number' ||
    typeof cy !== 'number' ||
    typeof stroke !== 'string' ||
    !payload
  ) {
    return null
  }

  const value = isAddition
    ? payload[ADDITIONAL_DATA_KEY]?.[dataKeyProperty]
    : payload[MAIN_DATA_KEY]?.[mainDataKeyProperty]
  const extraValue = isAddition
    ? payload[MAIN_DATA_KEY]?.[mainDataKeyProperty]
    : payload[ADDITIONAL_DATA_KEY]?.[`${dataKeyProperty}${ADDITION_SUFFIX}`]

  if (!value) {
    return null
  }

  return (
    <>
      <ThemeProvider mode={mode === ColorMode.LIGHT ? ColorMode.DARK : ColorMode.LIGHT}>
        <Tooltip
          // the tooltip only measures its anchor, which works for SVG elements too
          anchorRef={anchorRef as unknown as RefObject<HTMLElement | null>}
          open={isTooltipOpen}
          ref={disableTooltipContainerPointerEvents}
          minWidth={200}
          maxWidth={300}
          backgroundColor={Token.color.background}
          style={{
            boxShadow: Token.shadow.level4,
            pointerEvents: 'none',
          }}
        >
          <TooltipContent
            value={value}
            time={payload.date}
            extraValue={extraValue}
            primaryLabel={primaryLabel}
            additionLabel={additionLabel}
            entityName={entityName}
            dataKeyProperty={mainDataKeyProperty}
            isAddition={isAddition}
          />
        </Tooltip>
      </ThemeProvider>

      <svg
        ref={anchorRef}
        x={cx - OUTER_CIRCLE_SIZE}
        y={cy - OUTER_CIRCLE_SIZE}
        width={OUTER_CIRCLE_SIZE * 2}
        height={OUTER_CIRCLE_SIZE * 2}
        pointerEvents="none"
      >
        <circle
          cx={OUTER_CIRCLE_SIZE}
          cy={OUTER_CIRCLE_SIZE}
          r={OUTER_CIRCLE_SIZE / 2}
          stroke="none"
          fillOpacity={isTooltipOpen ? 0.1 : 0}
          fill={stroke}
        />
        <circle
          cx={OUTER_CIRCLE_SIZE}
          cy={OUTER_CIRCLE_SIZE}
          r={INNER_CIRCLE_RADIUS}
          stroke={stroke}
          strokeOpacity={isTooltipOpen ? 1 : strokeOpacity}
          strokeWidth={INNER_CIRCLE_STROKE}
          opacity={Number(isShown)}
          fill={Token.color.background}
        />
      </svg>
    </>
  )
}
